import {
    ActionTypes,
    SecondaryApiTypeEnum,
    TransactionStatusCode,
    TransactionStatusCodeMap,
    doesWorkflowRejectCodeExist,
    getWorkflowRejectCodeMessage
} from '@tassat/common-definitions';
import { FirmFormOptions, IFormValues } from '@tassat/forms/src/FirmForm/types';
import { CustomerAddFirmReq, CustomerModifyFirmReq } from '@tassat/fred-definitions';

import Worker from 'Lib/worker/PrimusWorkerWrapper';

export const submitFirmForm = async (values: IFormValues, opts: FirmFormOptions) => {
    const {
        actionType,
        firmId,
        editFirm,
        onClose,
        setSubmitError,
        features,
        initialValues
    } = opts;

    try {
        let response = { data: undefined };
        // TODO Update to handle multiple secondary api connections in the future
        const apiSecondaryConnectionTypeEnum = SecondaryApiTypeEnum.FIREBLOCKS;
        let isSecondaryApiEnabled = false;

        if (features?.fireblocks.api && values.APISecondaryConnectionType) {
            // TODO Update to handle multiple secondary api connections in the future
            isSecondaryApiEnabled = true;
        }

        // FRED-3378,FRED-3378 Add trim to Firm Name and Notes
        // Trim not needed for Email/LEI as validation does not allow spaces at all
        if (actionType === ActionTypes.add) {
            const sendValues: Omit<
                CustomerAddFirmReq,
                'RequestId' |
                'RequesterId' |
                'RequestType' |
                'RequestPattern' |
                'SuppressUserEmail' |
                'UUID' |
                'FirmId'
            > & {
                // Added for Fireblocks, modify for multiple api connections
                APISecondaryConnectionType?: SecondaryApiTypeEnum;
                IsSecondaryApiEnabled?: boolean;
            } = {
                Name: values.Name.trim(),
                FirmEmail: values.FirmEmail,
                Notes: values.Notes.trim(),
                Lei: values.Lei,
                CompanyCode: values.CompanyCode,
                IsTokenAPIFirm: values.IsTokenAPIFirm,
                IsSCFirm: values.IsSCFirm,
                IsLiquidityFacility: values.IsLiquidityFacility,
                IsCollateral: values.IsCollateral,
                IsDisabled: false,
                APISecondaryConnectionType: isSecondaryApiEnabled ? apiSecondaryConnectionTypeEnum : undefined,
                IsSecondaryApiEnabled: isSecondaryApiEnabled,
                CountryCode: values.Country?.value as string,
                StateCode: values.State?.value as string,
                EntitledAssets: (values.AssetMapping || []).map((asset) => ({ EntitledAssetsTicker: asset.value as string }))
            };
            if (sendValues.CompanyCode === null)
                delete sendValues.CompanyCode;

            if (!sendValues.APISecondaryConnectionType)
                delete sendValues.APISecondaryConnectionType;

            if (!sendValues.IsSecondaryApiEnabled)
                delete sendValues.IsSecondaryApiEnabled;

            response = await Worker.getInstance()
                .createFirm(sendValues);
        } else if (actionType === ActionTypes.edit) {
            const sendValues: Omit<
                CustomerModifyFirmReq,
                'RequestId' |
                'RequesterId' |
                'SuppressUserEmail' |
                'IsDisabled' |
                'UUID' |
                'FirmId'
            > & {
                // Added for Fireblocks, modify for multiple api connections
                APISecondaryConnectionType?: SecondaryApiTypeEnum | undefined;
                IsSecondaryApiEnabled?: boolean;
            } = {
                Name: values.Name.trim(),
                FirmEmail: values.FirmEmail,
                Notes: values.Notes ? values.Notes.trim() : '',
                Lei: values.Lei,
                CompanyCode: values.CompanyCode ?? '',
                IsTokenAPIFirm: values.IsTokenAPIFirm,
                IsSCFirm: values.IsSCFirm,
                IsLiquidityFacility: values.IsLiquidityFacility,
                IsCollateral: values.IsCollateral,
                IsClosed: editFirm.IsClosed,
                IsFrozen: editFirm.IsFrozen,
                IsLocked: editFirm.IsLocked,
                APISecondaryConnectionType: (isSecondaryApiEnabled || initialValues.APISecondaryConnectionType) ?
                    apiSecondaryConnectionTypeEnum : undefined,
                IsSecondaryApiEnabled: isSecondaryApiEnabled,
                CountryCode: values.Country?.value as string,
                StateCode: values.State?.value as string,
                EntitledAssets: (values.AssetMapping || []).map((asset) => ({ EntitledAssetsTicker: asset.value as string }))
            };
            if (sendValues.CompanyCode === null)
                delete sendValues.CompanyCode;

            response = await Worker.getInstance()
                .updateFirm(firmId, sendValues);
        }

        if (response.data && response.data.Status === 48) {
            onClose();
        } else if (response.data && response.data.Status === 49) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} Firm: ${response.data.Reason || 'Something went wrong'}`);
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} Firm: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        setSubmitError('Something went wrong');
    }
};

export const getTransactions = (firmId) => Worker.getInstance()
    .getTransactions({
        Condition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value}:firmId|EQ|${firmId}`,
        Sort: 'auditTime|DESC',
        PageNo: 1,
        RecordPerPage: 10
    });

export const getCollateral = async (firmId) => {
    const activeResult = await Worker.getInstance()
        .getCollateral({
            Condition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.ACTIVE]?.value}:PledgingFirmId|EQ|${firmId}^ReceivingFirmId|EQ|${firmId}`,
            Sort: 'AuditTime|DESC',
            PageNo: 1,
            RecordPerPage: 1
        });

    if (activeResult.data?.TotalCount > 0) {
        return activeResult;
    }

    return await Worker.getInstance()
        .getCollateral({
            Condition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value}:PledgingFirmId|EQ|${firmId}`,
            Sort: 'AuditTime|DESC',
            PageNo: 1,
            RecordPerPage: 1
        });
};

export const updateFirm = (firmId, sendValues) => Worker.getInstance()
    .updateFirm(firmId, sendValues);